// VBL (Vibration-Based Life) Analysis Types

import { FailureFilters } from './filters';

/**
 * VBL analysis method - only Johnson's method is supported
 * since VBL data is never censored
 */
export const VBL_ANALYSIS_METHOD = 'johnson' as const;

/**
 * VBL Forecast Constraint Settings
 * Controls VBL calculation behavior for forecasting
 */
export interface VblForecastConstraints {
  // Parameter Uncertainty Limits
  maxShapeCV: number;          // 0.05-0.20 based on sample size
  maxScaleCV: number;          // 0.03-0.15 based on sample size

  // Empirical Safety Factors
  medianSafetyFactor: number;  // 1.1-1.5 based on data quality
  p95SafetyFactor: number;     // 1.05-1.2 based on data quality

  // Survival Adaptation Settings
  useSurvivalAdaptation: boolean;      // Enable/disable survival adaptation
  survivalAdaptationStrength: number;  // 0.1-0.3
  earlyStageThreshold: number;         // 0.4-0.6

  // Auto-suggestion metadata
  recommendedBasedOn: 'sample-size' | 'user-custom';
  appliedAt: string; // ISO timestamp
}

/**
 * VBL Record - matches backend VBL model structure
 * Uses degradation_time instead of operation_time
 */
export interface VblRecord {
  failure_id: string;
  failure_mode_name: string;
  degradation_time: number; // in days (down_date - detected_date)
  detected_date: string;
  down_date: string | null;
  predicted_rts_date: string | null;
  actual_rts_date: string | null;
  install_date: string | null;
  case_id: string | number;
  turbine_id: string;
  turbine_name: string;
  model_id: string;
  model_name: string;
  cod_date: string | null;
  outcome_id: string;
  outcome_name: string;
  component_id: string;
  component_name: string;
  component_major: string; // For consistency with FailureRecord
  component_minor?: string | null; // For consistency with FailureRecord
  major_component_id: string;
  major_component_name: string;
  site_id: string;
  site_name: string;
  major_corrective: string;
  planned_reactive: string;
  inspection_severity: string;
  created_at: string;
  updated_at: string;

  // Computed fields for compatibility
  failure_mode: string; // alias for failure_mode_name
  failure_date: string; // alias for actual_rts_date or down_date
  degradation_time_years: number; // degradation_time converted to years
}

/**
 * VBL API Response - matches backend paginated response
 */
export interface VblApiResponse {
  total_count: number;
  total_pages: number;
  page: number;
  size: number;
  has_more: boolean;
  vbls: VblRecord[]; // Backend returns 'vbls' array
}

/**
 * VBL Analysis Result - Weibull analysis applied to degradation time
 * Matches WeibullAnalysisResult structure
 */
export interface VblAnalysisResult {
  parameters: {
    shape: number;
    scale: number;
    location?: number;
    // CI bounds are `number | null`: the server-side Johnson fit (Phase 2B)
    // yields non-finite bounds at the n==2 edge, which serialise to JSON null.
    shapeCI?: {
      lower: number | null;
      upper: number | null;
    };
    scaleCI?: {
      lower: number | null;
      upper: number | null;
    };
    rSquared?: number | null;
  };

  statistics?: {
    logLikelihood?: number;
    aic?: number;
    bic?: number;
    andersonDarling?: number;
    // Standard errors are `number | null`: non-finite at the n==2 edge
    // (Phase 2B server fit) and serialised as JSON null on the wire.
    standardErrors?: {
      shape: number | null;
      scale: number | null;
    };
  };

  summary: {
    sampleSize: number;
    meanLife: number;
    medianLife: number;
    characteristicLife: number;
    reliabilityAt?: {
      time: number;
      reliability: number;
    };
  };

  // Optional forecast constraint settings for VBL calculations
  forecastConstraints?: VblForecastConstraints;

  plotData: {
    empirical: Array<{
      x: number;
      y: number;
      time: number;
      probability: number;
      reliability?: number;
      // Core identifiers
      failure_id?: string;
      case_id?: string;
      // Turbine information
      turbine_id?: string;
      turbine_name?: string;
      model_id?: string;
      model_name?: string;
      // Site information
      site_id?: string;
      site_name?: string;
      // Component information
      component_id?: string;
      component_name?: string;
      major_component_id?: string;
      major_component_name?: string;
      component_major?: string;
      component_minor?: string | null;
      // Failure information
      failure_mode_name?: string;
      failure_mode?: string;
      // Outcome information
      outcome_id?: string;
      outcome_name?: string;
      // Date information
      detected_date?: string;
      down_date?: string | null;
      actual_rts_date?: string | null;
      predicted_rts_date?: string | null;
      install_date?: string | null;
      cod_date?: string | null;
      failure_date?: string;
      // Metadata
      created_at?: string;
      updated_at?: string;
      // Computed fields
      degradation_time_years?: number;
    }>;
    theoretical: Array<{
      x: number;
      y: number;
      time: number;
      probability: number;
    }>;
    confidenceIntervals?: {
      lower: Array<{ x: number; y: number }>;
      upper: Array<{ x: number; y: number }>;
    };
    censored?: Array<{
      x: number;
      y: number;
      time: number;
      probability: number;
      case_id?: string;
    }>;
  };

  metadata?: {
    method: typeof VBL_ANALYSIS_METHOD;
    convergence?: boolean;
    iterations?: number;
    message?: string;
  };
}

/**
 * VBL Analysis Request
 */
export interface VblAnalysisRequest {
  filters: FailureFilters;
  analysisParams: {
    confidenceLevel: number;
    method?: typeof VBL_ANALYSIS_METHOD;
  };
}

/**
 * VBL Workspace - specific to VBL analysis
 * Matches Workspace structure but with VBL-specific fields
 */
export interface VblWorkspace {
  id: string;
  name: string;
  description?: string;
  workspace_type: 'vbl';

  configuration: {
    filters: FailureFilters;
    analysisParams: {
      showConfidenceIntervals: boolean;
      confidenceLevel: number;
      showCensoredData: boolean;
      method?: typeof VBL_ANALYSIS_METHOD;
    };
  };

  dataSnapshot?: {
    vblRecords: VblRecord[];
    capturedAt: string;
    totalRecords: number;
    checksum?: string;
  };

  results?: VblAnalysisResult;

  metadata: {
    userId: string;
    createdAt: string;
    updatedAt: string;
    version: number;
    tags?: string[];
    dataContext: {
      totalAvailableRecords: number;
      dateRange: { from: string; to: string };
      sitesIncluded: string[];
      componentsAnalyzed: string[];
    };
  };

  createdBy?: {
    id: string;
    name: string;
    email?: string;
  };
}

/**
 * VBL API Request Types
 */
export interface SaveVblWorkspaceRequest {
  workspace_name: string;
  description?: string;
  tags?: string[];
  owner: string;
  workspace_type: 'vbl';
  configuration: VblWorkspace['configuration'];
  current_data: { vblRecords?: VblRecord[] };
  current_results: VblAnalysisResult | null;
}

export interface UpdateVblWorkspaceRequest {
  workspace_id: string;
  workspace_name?: string;
  description?: string;
  tags?: string[];
  workspace_type: 'vbl';
  configuration: VblWorkspace['configuration'];
  current_data: { vblRecords?: VblRecord[] };
  current_results: VblAnalysisResult | null;
  updated_by: string;
}

/**
 * Turbine to forecast - for VBL predictions
 */
export interface TurbineToForecast {
  failure_id: string;
  failure_mode_name: string | null;
  detected_date: string;
  predicted_rts_date: string | null;
  install_date: string | null;
  case_id: string | number;
  turbine_id: string;
  turbine_name: string;
  model_id: string;
  model_name: string;
  cod_date: string | null;
  outcome_id: string;
  outcome_name: string;
  component_id: string;
  component_name: string;
  major_component_id: string;
  major_component_name: string;
  site_id: string;
  site_name: string;
  inspection_severity: string;
  diagnostic_severity: string;
  created_at: string;
  updated_at: string;
}

export interface TurbineToForecastList {
  total_count: number;
  total_pages: number;
  page: number;
  size: number;
  has_more: boolean;
  turbines: TurbineToForecast[];
}